import React, { useMemo } from 'react';
import { Alert, Card, Col, List, Row, Space, Statistic, Typography, Button, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { buildCommandCenterPortfolio } from '@/components/epc/commercial-ux/commandCenterPortfolio';
import { EpcCommercialIntelligenceCompact } from '@/components/epc/commercial-ux/EpcCommercialIntelligenceCompact';

const { Title, Paragraph, Text } = Typography;

const LINKS = [
  {
    path: '/epc/commercial/control-tower',
    title: 'Control tower',
    desc: 'Queue metrics & pipeline health',
  },
  {
    path: '/growth/opportunities',
    title: 'Open a pursuit',
    desc: 'From an opportunity — continuous ECLE journey',
  },
  {
    path: '/epc/commercial/prequalifications?kind=EOI',
    title: 'Expression of Interest (EOI)',
    desc: 'Client invitation we respond to',
  },
  {
    path: '/epc/commercial/tenders?kind=RFQ',
    title: 'Request for Quotation (RFQ)',
    desc: 'Price a defined client scope',
  },
  {
    path: '/epc/commercial/tenders?kind=RFT',
    title: 'Request for Tender (RFT)',
    desc: 'Formal technical and commercial bid',
  },
  {
    path: '/epc/commercial/prequalifications',
    title: 'Prequalifications',
    desc: 'Expression of Interest and internal gates',
  },
  { path: '/epc/commercial/invitations/new', title: 'New invitation', desc: 'Register an EOI, RFQ, or RFT' },
  { path: '/epc/commercial/tenders', title: 'Tender register', desc: 'Client RFQ and RFT workspaces' },
  { path: '/epc/commercial/bid-decisions', title: 'Bid decisions', desc: 'Bid / no-bid governance' },
  { path: '/epc/commercial/awards', title: 'Awards', desc: 'LOI / award register' },
  { path: '/epc/commercial/contracts', title: 'Contracts', desc: 'EPC contract master' },
  { path: '/epc/commercial/subcontracts', title: 'Subcontracts', desc: 'Buy-side SCA / SPC admin' },
  { path: '/epc/commercial/mobilizations', title: 'Mobilizations', desc: 'Project activation' },
];

function urgencyColor(u: string): string | undefined {
  if (u === 'critical') return '#cf1322';
  if (u === 'attention') return '#d46b08';
  return undefined;
}

const EpcCommercialHubPage: React.FC = () => {
  const navigate = useNavigate();
  const dashQ = useQuery({
    queryKey: ['epc-commercial-dashboard'],
    queryFn: () => epcCommercialService.getDashboard(),
    staleTime: 30_000,
    retry: false,
  });

  const portfolio = useMemo(() => buildCommandCenterPortfolio(dashQ.data), [dashQ.data]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        EPC Commercial Command Center
      </Title>
      <Paragraph type="secondary">
        Portfolio view across Lead → Tender → Bid → Award → Contract → Mobilization. Open a{' '}
        <Text strong>Pursuit Workspace</Text> from an opportunity or tender for the continuous
        journey. KPIs are status counts — not invented win rates.
      </Paragraph>

      {dashQ.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={(dashQ.error as Error)?.message || 'Dashboard unavailable'}
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {portfolio.kpis.map((k) => (
          <Col xs={12} sm={8} md={4} key={k.id}>
            <Card
              size="small"
              hoverable
              onClick={() => navigate(k.href)}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={k.label}
                value={k.count}
                loading={dashQ.isLoading}
                valueStyle={urgencyColor(k.urgency) ? { color: urgencyColor(k.urgency) } : undefined}
              />
              {k.detail ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {k.detail}
                </Text>
              ) : null}
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="Needs attention">
            {!portfolio.attention.length ? (
              <Alert type="success" showIcon message="No portfolio attention items" />
            ) : (
              <List
                size="small"
                dataSource={portfolio.attention}
                renderItem={(a) => (
                  <List.Item
                    actions={[
                      <Button key="go" type="link" onClick={() => navigate(a.href)}>
                        Open
                      </Button>,
                    ]}
                  >
                    <Space wrap>
                      <Tag
                        color={
                          a.severity === 'critical'
                            ? 'error'
                            : a.severity === 'warning'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {a.severity}
                      </Tag>
                      <Text>{a.title}</Text>
                    </Space>
                    {a.detail ? (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {a.detail}
                        </Text>
                      </div>
                    ) : null}
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <EpcCommercialIntelligenceCompact title="Portfolio intelligence" />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {LINKS.map((l) => (
          <Col xs={24} sm={12} md={8} key={l.path}>
            <Card size="small" hoverable>
              <Space direction="vertical" size={4}>
                <Link to={l.path}>
                  <Text strong>{l.title}</Text>
                </Link>
                <Text type="secondary">{l.desc}</Text>
                <Link to={l.path}>
                  <Button type="link" style={{ padding: 0 }}>
                    Open
                  </Button>
                </Link>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default EpcCommercialHubPage;
